import type { Tool, ToolHandle, WhenInputRoleBindingSpec } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import type {
    MutableToolStep,
    MutableToolStepRoleAddress,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import type { Draft } from '@/workflow-builder/_contract/draft';
import type { RuntimeState, SlotCursor } from '@/workflow-builder/_contract/runtime';

function deriveCasePlaceholderIndex(args: {
    draftKind: 'branch' | 'while' | 'for';
    caseIndex: number;
    slot: 'when' | 'what';
}): number {
    if (args.draftKind === 'branch') {
        return args.slot === 'when' ? args.caseIndex : args.caseIndex + 1;
    }

    return args.slot === 'when' ? args.caseIndex + 1 : args.caseIndex;
}

export function createCaseDraftPlaceholderIndices(args: {
    draftKind: 'branch' | 'while' | 'for';
    caseIndex: number;
}) {
    return {
        placeholderIndexWhen: deriveCasePlaceholderIndex({
            draftKind: args.draftKind,
            caseIndex: args.caseIndex,
            slot: 'when',
        }),
        placeholderIndexWhat: deriveCasePlaceholderIndex({
            draftKind: args.draftKind,
            caseIndex: args.caseIndex,
            slot: 'what',
        }),
    };
}

export function createEmptyDraft(stepKind: 'tool' | 'branch' | 'while' | 'for'): Draft {
    switch (stepKind) {
        case 'tool':
            return {
                draftKind: 'tool',
                toolStep: null,
                workflowState: {}
            };
        case 'branch':
            return {
                draftKind: 'branch',
                caseSetDraft: {
                    caseSetKey: 'case-set-1',
                    cases: [
                        {
                            caseKey: 'case-1',
                            what: null,
                            when: null,
                            loopFeedbackBindingSpec: null,
                            ...createCaseDraftPlaceholderIndices({
                                draftKind: 'branch',
                                caseIndex: 0,
                            }),
                        },
                    ],
                    activeCaseIndex: 0,
                    defaultCase: null,
                },
                workflowState: {}
            };
        case 'while':
            return {
                draftKind: 'while',
                caseDraft: {
                    caseKey: 'case-1',
                    what: null,
                    when: null,
                    loopFeedbackBindingSpec: null,
                    ...createCaseDraftPlaceholderIndices({
                        draftKind: 'while',
                        caseIndex: 0,
                    }),
                },
                workflowState: {}
            };
        case 'for':
            return {
                draftKind: 'for',
                caseDraft: {
                    caseKey: 'case-1',
                    what: null,
                    when: null,
                    loopFeedbackBindingSpec: null,
                    ...createCaseDraftPlaceholderIndices({
                        draftKind: 'for',
                        caseIndex: 0,
                    }),
                },
                workflowState: {}
            };
    }
}

export function deriveInitialCursor(stepKind: 'tool' | 'branch' | 'while' | 'for'): SlotCursor {
    switch (stepKind) {
        case 'tool':
            return { cursorKind: 'self' };
        case 'while':
        case 'for':
            return { cursorKind: 'case/what' };
        case 'branch':
            return { cursorKind: 'cases/when', caseIndex: 0 };
    }
}

export function deriveWhenInputRoleBindingSpec(tool: Tool): WhenInputRoleBindingSpec | null {
    if (tool.templateToolHandle !== CONSTANTS.Handles.Tools.TOOL_LessThan) {
        return null;
    }

    return {
        dynamicInputRoleNames: [CONSTANTS.ToolRoleNames.TOOL_LessThan.LessThanSource],
        staticInputRoleNames: [CONSTANTS.ToolRoleNames.TOOL_LessThan.LessThanTarget],
    };
}

export function deriveActiveTargetAddress(state: RuntimeState): MutableToolStepRoleAddress | null {
    const selectedToolStep = state.selectionState.selectedToolStep;
    const selectedInputRoleName = state.selectionState.selectedInputRoleName;
    if (!selectedToolStep || !selectedInputRoleName) {
        return null;
    }

    return {
        stepKey: selectedToolStep.stepKey,
        roleName: selectedInputRoleName,
    };
}

export function writeToolStepToCursorInDraft(args: {
    draft: Draft;
    cursor: SlotCursor;
    toolStep: MutableToolStep;
}): Draft {
    const { draft, cursor, toolStep } = args;
    switch (cursor.cursorKind) {
        case 'self':
            if (draft.draftKind !== 'tool') return draft;
            return {
                ...draft,
                toolStep,
            };
        case 'case/what':
            if (draft.draftKind !== 'while' && draft.draftKind !== 'for') return draft;
            return {
                ...draft,
                caseDraft: {
                    ...draft.caseDraft,
                    what: toolStep,
                    placeholderIndexWhat: null,
                },
            };
        case 'case/when':
            if (draft.draftKind !== 'while' && draft.draftKind !== 'for') return draft;
            return {
                ...draft,
                caseDraft: {
                    ...draft.caseDraft,
                    when: toolStep,
                    placeholderIndexWhen: null,
                },
            };
        case 'cases/when': {
            if (draft.draftKind !== 'branch') return draft;
            const nextCases = [...draft.caseSetDraft.cases];
            const candidate = nextCases[cursor.caseIndex];
            if (!candidate) return draft;
            nextCases[cursor.caseIndex] = {
                ...candidate,
                when: toolStep,
                placeholderIndexWhen: null,
            };
            return {
                ...draft,
                caseSetDraft: {
                    ...draft.caseSetDraft,
                    cases: nextCases as typeof draft.caseSetDraft.cases,
                    activeCaseIndex: cursor.caseIndex,
                },
            };
        }
        case 'cases/what': {
            if (draft.draftKind !== 'branch') return draft;
            const nextCases = [...draft.caseSetDraft.cases];
            const candidate = nextCases[cursor.caseIndex];
            if (!candidate) return draft;
            nextCases[cursor.caseIndex] = {
                ...candidate,
                what: toolStep,
                placeholderIndexWhat: null,
            };
            return {
                ...draft,
                caseSetDraft: {
                    ...draft.caseSetDraft,
                    cases: nextCases as typeof draft.caseSetDraft.cases,
                    activeCaseIndex: cursor.caseIndex,
                },
            };
        }
        case 'branch/default/when': {
            if (draft.draftKind !== 'branch') return draft;
            const nextDefaultCase = {
                caseKey: draft.caseSetDraft.defaultCase?.caseKey ?? `case-default-${cursor.caseIndex + 1}`,
                when: toolStep,
                what: draft.caseSetDraft.defaultCase?.what ?? null,
                loopFeedbackBindingSpec: null,
                placeholderIndexWhen: null,
                placeholderIndexWhat: draft.caseSetDraft.defaultCase?.placeholderIndexWhat ?? null,
            };
            return {
                ...draft,
                caseSetDraft: {
                    ...draft.caseSetDraft,
                    defaultCase: nextDefaultCase,
                },
            };
        }
        case 'branch/default/what': {
            if (draft.draftKind !== 'branch' || !draft.caseSetDraft.defaultCase) return draft;
            return {
                ...draft,
                caseSetDraft: {
                    ...draft.caseSetDraft,
                    defaultCase: {
                        ...draft.caseSetDraft.defaultCase,
                        what: toolStep,
                        placeholderIndexWhat: null,
                    },
                },
            };
        }
        case 'branch/editing':
            return draft;
    }
}

export function deriveIdentityToolHandleFromResourceType(resourceTypeHandle: string): ToolHandle | null {
    if (!resourceTypeHandle.startsWith('TYPE-')) return null;
    const suffix = resourceTypeHandle.slice('TYPE-'.length);
    return `TOOL-${suffix}Identity` as ToolHandle;
}
